import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  useParams,
  useLocation,
  useNavigate,
  generatePath,
} from 'react-router-dom';
import {
  AccountType,
  GeneralLedgerSidepanel,
  PermissionEnum,
  TransactionTypeEnum,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearTransactionState } from '../../store/slices/transactions/gl';
import {
  useLazyGetCustomerGeneralLedgerTransactionQuery,
  useLazyGetCustomersAccountBalanceByIdQuery,
  useLazyGetCustomerStatementChangeLogsQuery,
} from '../../store/services/customers';
import { CustomerType } from '../../types';
import { StatementTransactionTypes } from './types';

import ROUTES from '../../../../routes';

const GLTransactionHOC = ({
  customerType,
  accountsPage,
}: StatementTransactionTypes): React.JSX.Element => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const {
    customersDictionaries: {
      customersTransactionTypes,
      currenciesList,
      baseCurrency,
      activePermissions,
    },
    customersStatmenetsTransactions: { gl },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);

  const [
    getDebitAccountBalanceById,
    { isLoading: debitBalanceIsLoading, isFetching: debitBalanceIsFetching },
  ] = useLazyGetCustomersAccountBalanceByIdQuery();
  const [
    getCreditAccountBalanceById,
    { isLoading: creditBalanceIsLoading, isFetching: creditBalanceIsFetching },
  ] = useLazyGetCustomersAccountBalanceByIdQuery();
  const [
    getTransactionChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetCustomerStatementChangeLogsQuery();

  const { transactionId, customerId } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const getTransactionChangeLogsAction = useCallback(
    (cursor?: string) => {
      getTransactionChangeLogs(
        `?take=20&entityType=GENERAL_LEDGER_TRANSACTION&entityId=${transactionId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [transactionId],
  );

  const linkedTransactionPath = useMemo(() => {
    const currentTransactionType = customersTransactionTypes?.find(
      (item) => item.id === gl?.transaction?.transaction?.typeId,
    );

    if (currentTransactionType) {
      let link = '#';

      if (
        currentTransactionType?.name === TransactionTypeEnum.INTERNAL &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_INTERNAL_LIST)
      ) {
        link = '/transactions/internal';
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.INCOMING &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_INCOMING_LIST)
      ) {
        link = '/transactions/incoming';
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.OUTGOING &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_OUTGOING_LIST)
      ) {
        link = '/transactions/outgoing';
      } else if (
        currentTransactionType?.name ===
          TransactionTypeEnum.CURRENCY_EXCHANGE &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_EXCHANGE_LIST)
      ) {
        link = '/transactions/exchange';
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.NOSTRO &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_NOSTRO_LIST)
      ) {
        link = '/transactions/nostro';
      } else if (
        currentTransactionType?.name === 'FEE' &&
        activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_LIST,
        )
      ) {
        link = '/transactions/general-ledger/service-fee';
      }

      return `${link}/${gl.transaction?.parentTransactionId}`;
    }

    return undefined;
  }, [
    customersTransactionTypes,
    gl?.transaction?.transaction?.typeId,
    gl.transaction?.parentTransactionId,
  ]);

  const handleClose = () => {
    setPanelIsOpen(false);

    let link = '#';

    if (customerType === CustomerType.INDIVIDUAL) {
      link = ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW_TRANSACTIONS;

      if (accountsPage) {
        link = ROUTES.CUSTOMERS_INDIVIDUAL_ACCOUNTS_TRANSACTIONS;
      }
    } else {
      link = ROUTES.CUSTOMERS_CORPORATE_OVERVIEW_TRANSACTIONS;

      if (accountsPage) {
        link = ROUTES.CUSTOMERS_CORPORATE_ACCOUNTS_TRANSACTIONS;
      }
    }

    setTimeout(() => {
      navigate(
        generatePath(`${link}${search || ''}`, {
          customerId: customerId as string,
        }),
      );
    }, 100);
  };

  const [getGeneralLedger, { isLoading, isFetching }] =
    useLazyGetCustomerGeneralLedgerTransactionQuery();

  const getTransaction = useCallback((id: string) => {
    getGeneralLedger(id);
  }, []);

  useEffect(() => {
    setPanelIsOpen(true);

    return () => {
      dispatch(clearTransactionState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!gl?.transaction && transactionId) {
      getTransaction(transactionId);
    }
  }, [getTransaction, gl?.transaction, transactionId]);

  useEffect(() => {
    if (transactionId && gl.transaction) {
      getDebitAccountBalanceById({
        id: gl.transaction?.debitAccountId as string,
        query: `&currencyId=${gl.transaction?.currencyId}`,
        typeAcc: AccountType.DEBIT,
      });
      getCreditAccountBalanceById({
        id: gl.transaction?.creditAccountId as string,
        query: `&currencyId=${gl.transaction?.currencyId}`,
        typeAcc: AccountType.CREDIT,
      });
    }
  }, [transactionId, gl.transaction]);

  return (
    <GeneralLedgerSidepanel
      panelIsOpen={panelIsOpen}
      handleClose={handleClose}
      transaction={gl.transaction}
      isLoading={isLoading || isFetching}
      title="GL Transaction"
      linkedTransactionPath={
        linkedTransactionPath?.includes('#') ? undefined : linkedTransactionPath
      }
      debitAccountBalance={gl?.debitAccountBalance}
      creditAccountBalance={gl?.creditAccountBalance}
      debitBalanceIsLoading={debitBalanceIsLoading || debitBalanceIsFetching}
      creditBalanceIsLoading={creditBalanceIsLoading || creditBalanceIsFetching}
      currenciesList={currenciesList}
      baseCurrency={baseCurrency}
      sidepanelExpandedKey="gl-statement"
      activePermission={activePermissions}
      users={users}
      changeLogsIsLoading={changeLogsIsLoading}
      changeLogsIsFetching={changeLogsIsFetching}
      getTransactionChangeLogs={getTransactionChangeLogsAction}
      transactionChangeLogsPagination={gl.transactionChangeLogsPagination}
      changeLogs={gl.transactionChangeLogs}
    />
  );
};

export default GLTransactionHOC;
